// components/StickyHeader.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min';
import './StickyHeader.css';

// 1. 提取静态数据到组件外部，避免重复创建
const PATH_PREFIX = '/company';

const NAV_ITEMS = [
  {
    name: '公司简介',
    path: '/',
    children: []
  },
  {
    name: '解决方案',
    path: `${PATH_PREFIX}/industrial-park`, // 默认跳转到第一个子项
    children: [
      { name: '工业园区', path: `${PATH_PREFIX}/industrial-park` },
      { name: '光储充换一体', path: `${PATH_PREFIX}/energy-storage` },
      { name: '综合体', path: `${PATH_PREFIX}/complex` },
      { name: '医院', path: `${PATH_PREFIX}/hospital` },
      { name: '台区', path: `${PATH_PREFIX}/power-district` },
      { name: '5G基站', path: `${PATH_PREFIX}/5g-base` },
      { name: '数据中心', path: `${PATH_PREFIX}/data-center` }
    ]
  },
  {
    name: '产品中心',
    path: `${PATH_PREFIX}/EnersafeBlock-263-L`,
    children: [
      { name: 'Enersafe Block-263-L', path: `${PATH_PREFIX}/EnersafeBlock-263-L` },
      { name: 'Enersafe Block-215-F', path: `${PATH_PREFIX}/Block-100-215-F` },
      { name: 'Energy Manage System', path: `${PATH_PREFIX}/EnergyManageSystem` },
    ]
  },
  {
    name: '新闻资讯',
    path: `${PATH_PREFIX}/news`,
    children: []
  },
  {
    name: '人力资源',
    path: `${PATH_PREFIX}/HumanResources`,
    children: []
  },
  {
    name: '联系我们',
    path: `${PATH_PREFIX}/contact`,
    children: []
  },
];

// 用于根据路径反推导航名称的映射表
// 注意：这里的 Key 必须与 App.js 中的 Route path 完全一致
const PATH_TO_NAME_MAP = {
  '/': '公司简介',
  [`${PATH_PREFIX}/industrial-park`]: '工业园区',
  [`${PATH_PREFIX}/energy-storage`]: '光储充换一体',
  [`${PATH_PREFIX}/complex`]: '综合体',
  [`${PATH_PREFIX}/hospital`]: '医院',
  [`${PATH_PREFIX}/power-district`]: '台区',
  [`${PATH_PREFIX}/5g-base`]: '5G基站',
  [`${PATH_PREFIX}/data-center`]: '数据中心',
  [`${PATH_PREFIX}/EnersafeBlock-263-L`]: 'EnersafeBlock-263-L',
  [`${PATH_PREFIX}/Block-100-215-F`]: 'EnersafeBlock-100-215-F',
  [`${PATH_PREFIX}/EnergyManageSystem`]: 'EnergyManageSystem',
  [`${PATH_PREFIX}/HumanResources`]: '人力资源',
  [`${PATH_PREFIX}/news`]: '新闻资讯',
  [`${PATH_PREFIX}/contact`]: '联系我们'
};

const StickyHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeItem, setActiveItem] = useState('公司简介');
  const [hoveredItem, setHoveredItem] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const hoverTimeoutRef = useRef(null);

  // 2. 优化滚动监听和路径匹配逻辑
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    // 初始化时执行一次
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 3. 单独处理路径变化时的 Active 状态更新
  useEffect(() => {
    const currentPath = location.pathname;
    // 先尝试精确匹配
    let matchedName = PATH_TO_NAME_MAP[currentPath];
    
    // 如果没有精确匹配，检查是否是某个父菜单的子项（用于高亮父菜单）
    if (!matchedName) {
      for (const item of NAV_ITEMS) {
        if (item.children.some(child => child.path === currentPath)) {
          matchedName = item.name;
          break;
        }
      }
    }

    setActiveItem(matchedName || '公司简介');
  }, [location.pathname]);

  const handleNavClick = (itemName, path) => {
    setActiveItem(itemName);
    navigate(path);
    setHoveredItem(null);
    
    // 只有当不是当前页时才滚动到顶部，避免重置用户浏览位置
    if (location.pathname !== path) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      });
    }
  };

  const handleMouseEnter = (itemName) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setHoveredItem(itemName);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
    }, 200);
  };

  const handleSubMenuMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleSubMenuMouseLeave = () => {
    handleMouseLeave();
  };

  return (
    <nav className={`navbar navbar-expand-lg fixed-top ${scrolled ? 'scrolled-navbar' : 'original-navbar'}`}>
      <div className="container">
        <a
          className="navbar-brand"
          href="#"
          onClick={(e) => {
            e.preventDefault();
            handleNavClick('公司简介', '/');
          }}
        >
          <img
            src={scrolled ? `${process.env.PUBLIC_URL}/imgs/layer.png` : `${process.env.PUBLIC_URL}/imgs/layer1.png`}
            alt="Logo"
            style={{ width: '130px' }}
          />
        </a>
        <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNavDropdown">
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarNavDropdown">
          <ul className="navbar-nav ms-auto">
            {NAV_ITEMS.map((item) => {
              // 判断当前项是否激活：名称匹配 或 子项路径匹配
              const isActive = activeItem === item.name || 
                               (item.children.length > 0 && item.children.some(child => child.path === location.pathname));

              return (
                <li
                  key={item.name}
                  className="nav-item"
                  onMouseEnter={() => item.children.length > 0 && handleMouseEnter(item.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.children.length > 0 ? (
                    <div className="nav-item-with-menu">
                      <a
                        className={`nav-link ${isActive ? 'active' : ''}`}
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          // 点击父菜单时，跳转到默认子项或指定路径
                          handleNavClick(item.name, item.path);
                        }}
                      >
                        {item.name}
                      </a>
                      {hoveredItem === item.name && (
                        <div
                          className="sub-menu"
                          onMouseEnter={handleSubMenuMouseEnter}
                          onMouseLeave={handleSubMenuMouseLeave}
                        >
                          <div className="sub-menu-content">
                            {item.children.map((child) => (
                              <a
                                key={child.name}
                                className={`sub-menu-item ${location.pathname === child.path ? 'active' : ''}`}
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleNavClick(child.name, child.path);
                                }}
                              >
                                {child.name}
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <a
                      className={`nav-link ${isActive ? 'active' : ''}`}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(item.name, item.path);
                      }}
                    >
                      {item.name}
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default StickyHeader;